import { useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { CHAIN_ROLES, computeDeploymentCost } from '@neopolis/engine';
import type { ChainRole, ChainSlot, Content, Equipment, GameState } from '@neopolis/engine';

import { T } from '../../textes-interface.js';
import { FicheEquipement } from './FicheEquipement.js';

function EquipementDisponible({
  equipement,
  selectionne,
  onSelectionner,
  onFiche,
}: {
  equipement: Equipment;
  selectionne: boolean;
  onSelectionner: () => void;
  onFiche: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: equipement.id });

  return (
    <div
      className={`flex items-center gap-1 rounded-xl border-2 ${
        selectionne ? 'border-sky-600 bg-sky-50' : 'border-slate-300 bg-white'
      } ${isDragging ? 'opacity-40' : ''}`}
    >
      <button
        {...attributes}
        {...listeners}
        ref={setNodeRef}
        type="button"
        aria-pressed={selectionne}
        onClick={onSelectionner}
        className="flex min-h-touch flex-1 cursor-grab items-center gap-2 px-3 py-2 text-left"
      >
        <span aria-hidden="true" className="text-xl">
          {equipement.icone}
        </span>
        <span className="min-w-0 flex-1 font-medium text-slate-800">{equipement.nom}</span>
        <span className="shrink-0 text-sm font-semibold tabular-nums text-slate-600">
          {equipement.cout}
        </span>
      </button>

      <button
        type="button"
        onClick={onFiche}
        aria-label={`${T.concevoir.fiche} : ${equipement.nom}`}
        className="min-h-touch min-w-touch rounded-lg text-sky-700 hover:bg-sky-50"
      >
        <span aria-hidden="true">ⓘ</span>
      </button>
    </div>
  );
}

function CaseDeChaine({
  definition,
  equipements,
  selection,
  onDeposer,
  onRetirer,
}: {
  definition: ChainSlot;
  equipements: readonly Equipment[];
  selection: string | null;
  onDeposer: () => void;
  onRetirer: (equipmentId: string) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: definition.id });

  return (
    <section
      ref={setNodeRef}
      aria-label={definition.nom}
      className={`rounded-2xl border-2 p-3 transition ${
        isOver ? 'border-sky-600 bg-sky-50' : 'border-slate-300 bg-white'
      }`}
    >
      <h3 className="font-bold text-slate-900">
        {definition.nom}
        {definition.obligatoire && (
          <span className="ml-2 text-xs font-semibold uppercase text-rose-600">
            {T.concevoir.obligatoire}
          </span>
        )}
      </h3>
      <p className="mt-1 text-sm text-slate-600">{definition.question}</p>

      <ul className="mt-2 space-y-2">
        {equipements.map((equipement) => (
          <li key={equipement.id}>
            <button
              type="button"
              onClick={() => onRetirer(equipement.id)}
              aria-label={`${T.concevoir.retirer} : ${equipement.nom}`}
              className="flex min-h-touch w-full items-center gap-2 rounded-xl bg-emerald-50 px-3 text-left font-medium text-emerald-900"
            >
              <span aria-hidden="true">{equipement.icone}</span>
              <span className="min-w-0 flex-1">{equipement.nom}</span>
              <span aria-hidden="true">✕</span>
            </button>
          </li>
        ))}
      </ul>

      <button
        type="button"
        disabled={selection === null}
        onClick={onDeposer}
        aria-label={`${T.concevoir.ajouterDans} ${definition.nom}`}
        className="mt-2 min-h-touch w-full rounded-xl border-2 border-dashed border-slate-300 px-3 text-sm font-semibold text-slate-600 disabled:opacity-40"
      >
        {T.concevoir.caseVide}
      </button>
    </section>
  );
}

/**
 * Étape 2 — Concevoir.
 *
 * Les quatre cases de la chaîne d'information à gauche, la bibliothèque des
 * quinze équipements à droite, filtrable par rôle. Le coût se met à jour à
 * chaque pose, en face du budget de la ville : la contrainte se voit avant le
 * refus du maire, pas après.
 */
export function EtapeConcevoir({
  content,
  partie,
  onPoser,
  onRetirer,
}: {
  content: Content;
  partie: GameState;
  onPoser: (slot: ChainRole, equipmentId: string) => void;
  onRetirer: (slot: ChainRole, equipmentId: string) => void;
}) {
  const [selection, setSelection] = useState<string | null>(null);
  const [filtre, setFiltre] = useState<ChainRole | null>(null);
  const [fiche, setFiche] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const parId = new Map(content.equipements.map((e) => [e.id, e]));
  const poses = new Set(CHAIN_ROLES.flatMap((role) => partie.chain[role]));
  const cases = content.chapitres.find((c) => c.id === 'ch2')?.cases_chaine ?? [];

  const disponibles = content.equipements.filter(
    (equipement) => !poses.has(equipement.id) && (filtre === null || equipement.role === filtre),
  );

  const cout = computeDeploymentCost(partie, content);
  const budget = partie.gauges.budget;
  const tropCher = cout > budget;

  const poser = (equipmentId: string): void => {
    const equipement = parId.get(equipmentId);
    if (equipement) {
      onPoser(equipement.role, equipmentId);
    }
    setSelection(null);
  };

  const surFinDeGlisser = (evenement: DragEndEvent): void => {
    if (typeof evenement.over?.id === 'string' && typeof evenement.active.id === 'string') {
      poser(evenement.active.id);
    }
  };

  return (
    <DndContext sensors={sensors} onDragEnd={surFinDeGlisser}>
      <div className="space-y-4">
        <section
          aria-live="polite"
          className={`flex flex-wrap items-baseline gap-x-4 rounded-xl px-4 py-3 ${
            tropCher ? 'bg-rose-50' : 'bg-white'
          } shadow-sm`}
        >
          <span className="font-semibold text-slate-800">{T.concevoir.coutTotal}</span>
          <span
            className={`text-2xl font-bold tabular-nums ${tropCher ? 'text-rose-700' : 'text-sky-700'}`}
          >
            {cout} {content.meta.unite_budget}
          </span>
          <span className="text-sm text-slate-600">
            {T.concevoir.budget} : {budget} {content.meta.unite_budget}
          </span>
          {tropCher && (
            <span role="alert" className="text-sm font-semibold text-rose-700">
              {T.concevoir.tropCher}
            </span>
          )}
        </section>

        <section aria-label={T.concevoir.chaine}>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {cases.map((definition) => (
              <CaseDeChaine
                key={definition.id}
                definition={definition}
                equipements={partie.chain[definition.id]
                  .map((id) => parId.get(id))
                  .filter((e): e is Equipment => e !== undefined)}
                selection={selection}
                onDeposer={() => selection && poser(selection)}
                onRetirer={(equipmentId) => onRetirer(definition.id, equipmentId)}
              />
            ))}
          </div>
        </section>

        {fiche && parId.get(fiche) && (
          <FicheEquipement
            content={content}
            equipement={parId.get(fiche)!}
            onFermer={() => setFiche(null)}
          />
        )}

        <section className="rounded-2xl border-2 border-slate-200 bg-white p-3">
          <h3 className="font-bold text-slate-900">{T.concevoir.bibliotheque}</h3>

          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              aria-pressed={filtre === null}
              onClick={() => setFiltre(null)}
              className={`min-h-touch rounded-xl border-2 px-4 font-semibold ${
                filtre === null
                  ? 'border-sky-600 bg-sky-600 text-white'
                  : 'border-slate-300 bg-white text-slate-700'
              }`}
            >
              {T.concevoir.filtreTous}
            </button>

            {cases.map((definition) => (
              <button
                key={definition.id}
                type="button"
                aria-pressed={filtre === definition.id}
                onClick={() => setFiltre(filtre === definition.id ? null : definition.id)}
                className={`min-h-touch rounded-xl border-2 px-4 font-semibold ${
                  filtre === definition.id
                    ? 'border-sky-600 bg-sky-600 text-white'
                    : 'border-slate-300 bg-white text-slate-700'
                }`}
              >
                {definition.nom}
              </button>
            ))}
          </div>

          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {disponibles.map((equipement) => (
              <li key={equipement.id}>
                <EquipementDisponible
                  equipement={equipement}
                  selectionne={selection === equipement.id}
                  onSelectionner={() =>
                    setSelection((precedent) =>
                      precedent === equipement.id ? null : equipement.id,
                    )
                  }
                  onFiche={() => setFiche(equipement.id)}
                />
              </li>
            ))}
          </ul>
        </section>
      </div>
    </DndContext>
  );
}
